import { Head, Link, router } from "@inertiajs/react"
import DOMPurify from "dompurify"
import { AlertTriangle, Check, Trash2 } from "lucide-react"
import { useEffect, useMemo, useState } from "react"

import HeadingSmall from "@/components/heading-small"
import { HpiPagination } from "@/components/hpi/data-table"
import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/app-layout"
import {
  destroyReadNotificationsPath,
  markAllReadNotificationsPath,
  markAsReadNotificationPath,
  notificationPath,
  notificationsPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

const PER_PAGE = 50

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Notifications", href: notificationsPath() },
]

interface Notification {
  id: number
  type: string
  subject: string | null
  message: string
  body: string | null
  url: string | null
  urgent: boolean
  sender_name: string | null
  read_at: string | null
  created_at: string
}

interface NotificationsProps {
  notifications: Notification[]
  unread_count: number
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return (
    date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }) +
    " " +
    date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
  )
}

export default function Index({
  notifications,
  unread_count,
}: NotificationsProps) {
  const [now, setNow] = useState<number | null>(null)
  const [page, setPage] = useState(1)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  // Reset to page 1 when notifications change (e.g. after delete/mark-read)
  const notificationKey = useMemo(
    () => notifications.map((n) => n.id).join(","),
    [notifications],
  )
  const [prevKey, setPrevKey] = useState(notificationKey)
  if (prevKey !== notificationKey) {
    setPrevKey(notificationKey)
    if (page !== 1) setPage(1)
  }

  useEffect(() => {
    const id = requestAnimationFrame(() => setNow(Date.now()))
    return () => cancelAnimationFrame(id)
  }, [])
  const timeAgo = (dateString: string) => {
    if (now === null) return "..."
    const seconds = Math.floor((now - new Date(dateString).getTime()) / 1000)
    if (seconds < 60) return "just now"
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    return `${days}d ago`
  }

  const totalPages = Math.ceil(notifications.length / PER_PAGE)
  const paginated = notifications.slice((page - 1) * PER_PAGE, page * PER_PAGE)
  const hasReadNotifications = notifications.some((n) => n.read_at)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Notifications" />

      <div className="mx-auto max-w-3xl space-y-6 p-6">
        <div className="flex items-center justify-between">
          <HeadingSmall
            title="Activity Messages"
            description={`${unread_count} unread`}
          />
          <div className="flex items-center gap-2">
            {hasReadNotifications && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => router.delete(destroyReadNotificationsPath())}
              >
                Delete All Read
              </Button>
            )}
            {unread_count > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => router.patch(markAllReadNotificationsPath(), {})}
              >
                Mark All Activity Messages as Read
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-3">
          {notifications.length === 0 && (
            <div className="text-hpi-med-gray border-hpi-light-gray rounded-lg border px-4 py-12 text-center text-sm">
              No unread Activity Messages found
            </div>
          )}

          {paginated.map((notification) => (
            <div
              key={notification.id}
              className={`group flex items-start gap-3 rounded-lg border px-4 py-3 transition-shadow hover:shadow-sm ${
                !notification.read_at
                  ? "border-hpi-active-blue/30 bg-hpi-pale-blue/50"
                  : "border-hpi-light-gray bg-white"
              }`}
            >
              {/* Urgency / Unread indicator */}
              <div className="mt-1 w-5 shrink-0">
                {notification.urgent ? (
                  <AlertTriangle className="text-hpi-red h-5 w-5" />
                ) : !notification.read_at ? (
                  <div className="mt-0.5 flex justify-center">
                    <div className="bg-hpi-active-blue h-2 w-2 rounded-full" />
                  </div>
                ) : null}
              </div>

              {/* Content */}
              <div className="min-w-0 flex-1">
                {/* Header: date, sender */}
                <div className="text-hpi-med-gray flex items-center gap-2 text-xs">
                  <span>{formatDate(notification.created_at)}</span>
                  {notification.sender_name && (
                    <>
                      <span>by</span>
                      <span className="font-medium">
                        {notification.sender_name}
                      </span>
                    </>
                  )}
                  <span className="ml-auto">
                    {timeAgo(notification.created_at)}
                  </span>
                </div>

                {/* Subject */}
                {notification.subject && (
                  <p
                    className={`mt-1 text-sm font-semibold ${
                      notification.urgent
                        ? "text-hpi-red"
                        : "text-hpi-dark-gray"
                    }`}
                  >
                    {notification.urgent && (
                      <AlertTriangle className="text-hpi-red mr-1 inline h-4 w-4 align-text-bottom" />
                    )}
                    {notification.subject}
                  </p>
                )}

                {/* Message / body */}
                {notification.body ? (
                  <div
                    className="text-hpi-dark-gray [&_a]:text-hpi-active-blue mt-1 text-sm [&_a]:underline"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(notification.body),
                    }}
                  />
                ) : (
                  <p className="text-hpi-dark-gray mt-1 text-sm">
                    {notification.url ? (
                      <Link
                        href={notification.url}
                        className="hover:text-hpi-active-blue"
                      >
                        {notification.message}
                      </Link>
                    ) : (
                      notification.message
                    )}
                  </p>
                )}

                {/* Link */}
                {notification.url && (
                  <p className="mt-1 text-xs">
                    <span className="text-hpi-med-gray">Link: </span>
                    <Link
                      href={notification.url}
                      className="text-hpi-active-blue hover:underline"
                      target="_blank"
                    >
                      {notification.url}
                    </Link>
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="flex shrink-0 items-center gap-1">
                {!notification.read_at && (
                  <button
                    onClick={() =>
                      router.patch(
                        markAsReadNotificationPath({ id: notification.id }),
                        {},
                      )
                    }
                    className="text-hpi-med-gray hover:bg-hpi-light-gray hover:text-hpi-dark-green rounded p-1 opacity-0 transition-opacity group-hover:opacity-100"
                    title="Mark as read"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                )}
                <button
                  onClick={() => {
                    setDeletingId(notification.id)
                    router.delete(notificationPath({ id: notification.id }), {
                      onFinish: () => setDeletingId(null),
                    })
                  }}
                  disabled={deletingId === notification.id}
                  className="text-hpi-med-gray hover:bg-hpi-light-gray hover:text-hpi-red rounded p-1 opacity-0 transition-opacity group-hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-50 disabled:group-hover:opacity-50"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}

          {notifications.length > PER_PAGE && (
            <HpiPagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={notifications.length}
              perPage={PER_PAGE}
              onPageChange={setPage}
            />
          )}
        </div>
      </div>
    </AppLayout>
  )
}
